import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

interface Product {
  title: string;
  category: string;
  description?: string;
  image?: string;
  image_url?: string;
  images?: { image_url: string; image: string }[];
}

interface Props {
  show: boolean;
  products: Product[];
  startIndex?: number;
  onClose: () => void;
}

const ProductCarouselModal = ({ show, products, startIndex = 0, onClose }: Props) => {
  const [prodIdx, setProdIdx] = useState(startIndex);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setProdIdx(startIndex);
    setImgIdx(0);
  }, [startIndex, show]);

  const product = products[prodIdx];
  const images: string[] = product?.images?.map((i) => i.image_url).filter(Boolean) ||
    (product?.image_url ? [product.image_url] : []);

  const currentImg = images[imgIdx] || null;

  return (
    <Modal show={show} onHide={onClose} centered contentClassName="rounded-4 overflow-hidden">
      {/* Header */}
      <Modal.Header closeButton className="border-bottom px-4 py-3">
        <Modal.Title className="fs-6 fw-semibold">{product?.title || "Products"}</Modal.Title>
      </Modal.Header> 

      {/* Image area */}
      <div style={{ background: "#000", height: 260, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {currentImg ? (
          <img
            src={currentImg}
            alt={product?.title}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <span className="text-muted" style={{ fontSize: 13 }}>No image available</span>
        )}

        {/* Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => setImgIdx((i) => i - 1)}
              disabled={imgIdx === 0}
              style={arrowStyle("left")}
            >&#8249;</button>
            <button
              onClick={() => setImgIdx((i) => i + 1)}
              disabled={imgIdx === images.length - 1}
              style={arrowStyle("right")}
            >&#8250;</button>
          </>
        )}

        {/* Counter */}
        {images.length > 0 && (
          <span style={{ position: "absolute", bottom: 8, right: 12, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 11, padding: "2px 8px", borderRadius: 10 }}>
            {imgIdx + 1} / {images.length}
          </span>
        )}
      </div>

      {/* Image dots */}
      {images.length > 1 && (
        <div className="d-flex justify-content-center gap-1 pt-2">
          {images.map((_, i) => (
            <div
              key={i}
              onClick={() => setImgIdx(i)}
              style={{ width: 6, height: 6, borderRadius: "50%", cursor: "pointer", background: i === imgIdx ? "#1e5eff" : "#ccc" }}
            />
          ))}
        </div>
      )}

      {/* Product info */}
      <Modal.Body className="px-4 pt-3 pb-2">
        <span
          className="d-inline-block mb-2"
          style={{ background: "#f1f3f5", color: "#555", fontSize: 11, padding: "2px 10px", borderRadius: 10 }}
        >
          {product?.category}
        </span>
        {product?.description && (
          <p
            className="text-muted mb-0"
            style={{ fontSize: 13, lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
          >
            {product.description}
          </p>
        )}
      </Modal.Body>

      {/* Product nav dots (if multiple products) */}
      {products.length > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2 pb-3 pt-1">
          <button className="btn btn-sm btn-outline-secondary rounded-circle p-0" style={{ width: 26, height: 26 }}
            disabled={prodIdx === 0} onClick={() => { setProdIdx((p) => p - 1); setImgIdx(0); }}>‹</button>
          {products.map((_, i) => (
            <div key={i} onClick={() => { setProdIdx(i); setImgIdx(0); }}
              style={{ width: 6, height: 6, borderRadius: "50%", cursor: "pointer", background: i === prodIdx ? "#66C15E" : "#ccc" }} />
          ))}
          <button className="btn btn-sm btn-outline-secondary rounded-circle p-0" style={{ width: 26, height: 26 }}
            disabled={prodIdx === products.length - 1} onClick={() => { setProdIdx((p) => p + 1); setImgIdx(0); }}>›</button>
        </div>
      )}
    </Modal>
  );
};

const arrowStyle = (side: "left" | "right"): React.CSSProperties => ({
  position: "absolute", top: "50%", transform: "translateY(-50%)",
  [side]: 10, background: "rgba(255,255,255,0.18)", border: "none",
  borderRadius: "50%", width: 36, height: 36, display: "flex",
  alignItems: "center", justifyContent: "center", cursor: "pointer",
  fontSize: 20, color: "#fff", zIndex: 2,
});

export default ProductCarouselModal;